import { Link } from 'react-router-dom';
import { Heart, AlertTriangle } from 'lucide-react';
import { getImageAlt, generateSrcSet, urlForOptimized } from '../../lib/sanity';
import { ShareButtons } from '../common/ShareButtons';
import { getPetUrlIdentifier, getPetFullUrl } from '../../lib/petUtils';
import type { Pet } from '../../types/sanity';

interface PetCardProps {
  pet: Pet;
  onMeetPet: (identifier: string) => void;
  calculateAge: (birthDate: string) => string;
}

export function PetCard({ pet, onMeetPet, calculateAge }: PetCardProps) {
  const truncateDescription = (text: string | null | undefined, maxLength: number = 160) => {
    if (!text) return 'Brak opisu';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + '...';
  };

  return (
    <div className="bg-white dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg shadow-lg overflow-hidden flex flex-col">
      <div
        onClick={() => onMeetPet(getPetUrlIdentifier(pet))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onMeetPet(getPetUrlIdentifier(pet));
          }
        }}
        className="relative aspect-video cursor-pointer overflow-hidden"
        role="button"
        tabIndex={0}
        aria-label={`Zobacz profil ${pet.name}`}
      >
        <img
          src={urlForOptimized(pet.image, 800, 450, 80) as string}
          srcSet={generateSrcSet(pet.image)}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          alt={getImageAlt(pet.image, pet)}
          className="w-full h-full object-cover object-center"
          loading="lazy"
          decoding="async"
          width="800"
          height="450"
        />
        {pet.gallery && pet.gallery.length > 0 && (
          <div className="absolute bottom-4 left-4 flex gap-1">
            {pet.gallery.map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full bg-white/80 dark:bg-gray-800/80"
              />
            ))}
          </div>
        )}
        {pet.isUrgentFundraising && (
          <div className="absolute top-4 right-4 bg-accessible-red-600 text-white px-3 py-1 rounded-full flex items-center gap-1 text-sm font-bold animate-pulse shadow-md">
            <AlertTriangle className="w-4 h-4" />
            <span>PILNE</span>
          </div>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="heading-4 text-accessible-gray-900 dark:text-white high-contrast:text-white mb-0">
              {pet.name}
            </h3>
            <span className="px-3 py-1 bg-accessible-red-600/10 dark:bg-white/10 text-accessible-red-600 dark:text-white rounded-full body-small no-margin">
              {calculateAge(pet.birthDate)}
            </span>
          </div>
        </div>
        {pet.tags && pet.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {pet.tags.map((tag, index) => (
              <span
                key={tag._ref || tag.slug?.current || `tag-${index}`}
                className="px-2 py-1 bg-accessible-red-600/10 dark:bg-white/10 text-accessible-red-600 dark:text-white rounded-full text-sm"
              >
                {tag.label}
              </span>
            ))}
          </div>
        )}
        <p className="body-medium flex-1">
          {truncateDescription(pet.description)}
        </p>
        
        {pet.isUrgentFundraising && pet.fundraisingGoal && (
          <div className="mt-4 p-3 bg-accessible-red-600/10 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-accessible-red-600 dark:text-accessible-red-500 high-contrast:text-accessible-red-600">Pilna zbiórka:</span>
              <span className="text-sm font-bold text-accessible-red-600 dark:text-accessible-red-500 high-contrast:text-white">
                {Math.round((pet.fundraisingCurrent || 0) / pet.fundraisingGoal * 100)}%
              </span>
            </div>
            <div className="h-2 bg-accessible-gray-200 dark:bg-accessible-gray-600 high-contrast:bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-accessible-red-600 dark:bg-accessible-red-500 high-contrast:bg-white rounded-full" 
                style={{ width: `${Math.round((pet.fundraisingCurrent || 0) / pet.fundraisingGoal * 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-accessible-red-600 dark:text-accessible-red-600 high-contrast:text-accessible-red-600 mt-1">
              <span className="urgent-fundraising-color">{pet.fundraisingDescription}</span>
            </p>
          </div>
        )}
        <button 
          onClick={() => onMeetPet(getPetUrlIdentifier(pet))}
          className="mt-4 flex items-center justify-center gap-2 w-full bg-accessible-red-600 hover:bg-accessible-red-700 text-white py-2 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-accessible-red-600 dark:focus:ring-offset-accessible-gray-700"
          aria-label={`Poznaj ${pet.name}`}
          title={`Poznaj bliżej ${pet.name}`}
          role="button"
        >
          <Heart className="w-5 h-5" aria-hidden="true" />
          <span className="btn-text-medium">Poznaj {pet.name}</span>
        </button>
        
        {pet.isUrgentFundraising && (
          <Link
            to="/wplata-jednorazowa"
            className="mt-2 flex items-center justify-center gap-2 w-full bg-yellow-500 hover:bg-yellow-600 text-accessible-gray-900 py-2 px-4 rounded-lg transition-colors font-bold text-sm shadow-md"
            title={`Wesprzyj pilną zbiórkę dla ${pet.name}`}
          >
            <AlertTriangle className="w-4 h-4" />
            <span>WESPRZYJ PILNĄ ZBIÓRKĘ</span>
          </Link>
        )}

        <div className="mt-4 pt-4 border-t border-accessible-gray-200 dark:border-accessible-gray-700 high-contrast:border-white/20">
          <ShareButtons
            url={getPetFullUrl(pet)}
            title={`Poznaj ${pet.name} – ${pet.mainCategory === 'dog' ? 'pies' : 'kot'} do adopcji`}
            description={pet.description}
          />
        </div>
      </div>
    </div>
  );
}